import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ESignatureDialog from "@/components/ESignatureDialog";
import AuditTrailPanel from "@/components/AuditTrailPanel";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowRight, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATES = ["Reported", "UnderInvestigation", "RootCauseIdentified", "Closed"];

export default function Deviations() {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [form, setForm] = useState({ title: "", description: "", severity: "Medium", process_area: "Production" });
  const [esignOpen, setEsignOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const load = async () => setList((await api.get("/deviations")).data);
  useEffect(() => { load(); }, []);
  const loadDetail = async (id) => setDetail((await api.get(`/deviations/${id}`)).data);

  const create = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      await api.post("/deviations", form);
      toast.success("Deviation logged");
      setOpen(false);
      setForm({ title: "", description: "", severity: "Medium", process_area: "Production" });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && e.response?.status !== 403 && e.response?.status < 500) {
        toast.error(typeof detail === "string" ? detail : "Log failed");
      }
    }
  };

  const requestTransition = (id, toState) => {
    setDetail({ ...detail, deviation_id: id });
    setPending({ id, toState });
    setEsignOpen(true);
  };

  const onSigned = async (sig) => {
    await api.post(`/deviations/${pending.id}/transition`, {
      to_state: pending.toState, signature_id: sig.signature_id, notes: sig.reason,
    });
    toast.success(`Advanced to ${pending.toState}`);
    setPending(null);
    load();
    if (detail?.deviation_id === pending.id) loadDetail(pending.id);
    setRefreshKey((k) => k + 1);
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="vq-overline mb-2">Quality Events</div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">Deviations</h1>
            <p className="text-slate-600 mt-2">Non-conformance / quality event management</p>
          </div>
          <Button onClick={() => setOpen(true)} data-testid="dev-new-btn" className="h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Report Deviation
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2 vq-card overflow-hidden">
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 vq-overline">All Deviations ({list.length})</div>
            <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
              {list.map((d) => (
                <button key={d.deviation_id} data-testid={`dev-row-${d.deviation_id}`}
                        onClick={() => loadDetail(d.deviation_id)}
                        className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${detail?.deviation_id === d.deviation_id ? "bg-slate-100" : ""}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono ${d.severity === "High" || d.severity === "Critical" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"}`}>{d.severity}</span>
                    <span className="text-[10px] font-mono text-slate-500">{d.state}</span>
                  </div>
                  <div className="font-semibold text-sm text-slate-900 truncate">{d.title}</div>
                  <div className="text-[10px] font-mono text-slate-400">{d.deviation_id}</div>
                </button>
              ))}
              {list.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No deviations.</div>}
            </div>
          </div>

          <div className="lg:col-span-3">
            {detail ? (
              <>
                <div className="vq-card p-6 mb-4">
                  <div className="vq-overline mb-1 font-mono">{detail.deviation_id}</div>
                  <h2 className="font-display font-bold text-2xl text-slate-900">{detail.title}</h2>
                  <p className="text-slate-600 mt-2 mb-4">{detail.description}</p>
                  <div className="flex items-center gap-2 flex-wrap mb-4">
                    {STATES.map((s, i) => {
                      const idx = STATES.indexOf(detail.state);
                      return (
                        <div key={s} className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-[10px] font-mono ${
                            s === detail.state ? "bg-slate-900 text-white" : i < idx ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"
                          }`}>{s}</span>
                          {i < STATES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
                        </div>
                      );
                    })}
                  </div>
                  {STATES.indexOf(detail.state) < STATES.length - 1 && (
                    <Button onClick={() => requestTransition(detail.deviation_id, STATES[STATES.indexOf(detail.state) + 1])}
                            data-testid="dev-transition-btn"
                            className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                      <ShieldCheck className="w-4 h-4 mr-2" /> Advance — with e-sig
                    </Button>
                  )}
                </div>
                <AuditTrailPanel entityType="deviation" entityId={detail.deviation_id} refreshKey={refreshKey} />
              </>
            ) : (
              <div className="vq-card p-10 text-center text-slate-500">Select a deviation to view details.</div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Report Deviation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold">Title</Label>
              <Input value={form.title} data-testid="dev-form-title"
                     onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" /></div>
            <div><Label className="text-xs font-semibold">Description</Label>
              <Textarea value={form.description} data-testid="dev-form-desc"
                        onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold">Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Medium", "High", "Critical"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select></div>
              <div><Label className="text-xs font-semibold">Process area</Label>
                <Select value={form.process_area} onValueChange={(v) => setForm({ ...form, process_area: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Production", "Design", "Clinical", "Software", "Supplier"].map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}</SelectContent>
                </Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="dev-form-submit" className="bg-slate-900 hover:bg-[#0055FF] text-white">Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {pending && (
        <ESignatureDialog
          open={esignOpen} onOpenChange={setEsignOpen}
          entityType="deviation" entityId={pending.id}
          action={`deviation_transition_${pending.toState}`}
          defaultMeaning="Review"
          title={`Sign to advance to ${pending.toState}`}
          onSigned={onSigned}
        />
      )}
    </DashboardLayout>
  );
}
